import { useContext, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { SpinnerContainer } from '@zg-rentals/rental-manager-env';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Box, Button, ButtonGroup, Heading, Image, Modal, Paragraph } from '@zillow/constellation-10';

import { ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing } from '@zg-rentals/ts-z-calendar-client-react-query';
import { TourAvailabilityWizard } from '@zg-rentals/touring';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { RMWContext } from '../../../providers/RMWContext';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import propertyUtils from '../../../utils/propertyUtils';
import { getFullCalendarPageUrl } from '../../../utils/touringUtils';

import PostListingWorkflowCSEvents from '../../../model/ClickstreamEvent/PostListingWorkflowCSEvents';

import PropertyConstants from '../../../constants/PropertyConstants';

import useAccessPostingPathTourScheduling from '../../../hooks/useAccessPostingPathTourScheduling';

import rentHome from '../../../images/rent-home-people.png';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const PostActivationDashboardModal = ({ isOpen, onClose }: Props) => {
  const [displayTourWizard, setDisplayTourWizard] = useState(false);
  const queryClient = useQueryClient();
  const history = useHistory();
  const { canAccessPostingPathTourScheduling } = useAccessPostingPathTourScheduling();

  const {
    modularListingState: { canAccessScreeningCriteriaInListingFlow },
  } = useContext(ModularListingContext);
  const { paths } = useContext(RMWContext);

  const {
    user,
    currentProperty,
    daysExtended,
    isFreemiumEnabled,
    type: freemiumActiveProductType,
    duration: freemiumActiveProductDuration,
    listingTypeCode,
    listingAlias,
  } = useSelector((state) => {
    return {
      user: state.user,
      currentProperty: state.properties.currentProperty,
      // @ts-expect-error
      daysExtended: state?.properties?.postActivationState?.daysExtended,
      isFreemiumEnabled: state?.properties?.currentProperty?.freemium?.isEnabled,
      type: state?.properties?.currentProperty?.freemium?.activeProduct?.type,
      duration: state?.properties?.currentProperty?.freemium?.activeProduct?.duration,
      listingTypeCode: state?.properties?.currentProperty?.listingTypeCode,
      listingAlias: state?.properties?.currentProperty?.listingAlias,
    };
  }, shallowEqual);

  const { data: touringTreatment, isLoading: isGetTouringTreatmentFetching } =
    useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing(
      {
        listingAlias,
      },
      { enabled: Boolean(listingAlias) },
    );
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const isActiveProductTypePremium =
    isFreemiumEnabled && freemiumActiveProductType === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty: { listingTypeCode } });

  const shouldDisplayTourCalendar = canAccessPostingPathTourScheduling && touringTreatment?.isTouringEnabledForListing;
  const shouldDisplayTourWizard =
    !canAccessPostingPathTourScheduling &&
    !touringTreatment?.isTouringEnabledForListing &&
    !touringTreatment?.hasAvailability;
  const shouldDisplayTouring = shouldDisplayTourCalendar || shouldDisplayTourWizard;

  let networkSites = 'Zillow, HotPads, and Trulia';
  if (isRoomForRent) {
    networkSites = 'Zillow and HotPads';
  }

  useEffect(() => {
    if (shouldDisplayTouring) {
      PostListingWorkflowCSEvents.PostListingDashboardModalSetTourAvailabilityImpression({
        currentProperty,
        user,
      });
    }
  }, [shouldDisplayTouring]);

  const handleGotItClick = () => {
    onClose();
  };

  const handleSetTourAvailabilityClick = () => {
    PostListingWorkflowCSEvents.PostListingDashboardModalSetTourAvailabilityButton({ currentProperty, user });

    if (shouldDisplayTourWizard) {
      setDisplayTourWizard(true);
    } else {
      history.push(getFullCalendarPageUrl(listingAlias, paths));
      onClose();
    }
  };

  const onTourAvailabilityWizardClose = () => {
    setDisplayTourWizard(false);
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path,
    });
    onClose();
  };

  const getButtonCTA = () => {
    let buttonText = 'Visit your overview';
    let onClick = handleGotItClick;

    if (shouldDisplayTouring) {
      buttonText = shouldDisplayTourWizard ? 'Set tour availability' : 'Add more availability';
      onClick = handleSetTourAvailabilityClick;
    } else if (canAccessScreeningCriteriaInListingFlow) {
      buttonText = 'Got it';
      onClick = handleGotItClick;
    }

    return (
      <Button
        size="lg"
        fluid={isScreenMobile}
        onClick={onClick}
        data-eventid={
          shouldDisplayTouring
            ? PostListingWorkflowCSEvents.PostListingDashboardModalSetTourAvailabilityButton.data_event_id
            : undefined
        }
        tone="brand"
        emphasis="filled"
      >
        {buttonText}
      </Button>
    );
  };

  if (isGetTouringTreatmentFetching) {
    return <SpinnerContainer />;
  }

  if (displayTourWizard) {
    return (
      <TourAvailabilityWizard
        onClose={onTourAvailabilityWizardClose}
        listingAlias={listingAlias}
        modalHeaderText={currentProperty.street || currentProperty.address?.street}
        clickStreamMetadata={{ currentProperty, user }}
        canAccessPostingPathTourDuration={true}
      />
    );
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'md', lg: 'lg' }}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      dividers={false}
      body={
        <Box
          data-eventid={
            shouldDisplayTouring
              ? PostListingWorkflowCSEvents.PostListingDashboardModalSetTourAvailabilityImpression.data_event_id
              : undefined
          }
        >
          <Image
            src={rentHome}
            alt=""
            css={{ display: 'block', marginInline: 'auto', marginBottom: '600', width: '60%' }}
          />

          <Heading level={1} textStyle="heading-sm" css={{ marginBottom: '400' }}>
            Congratulations, you published your listing!
          </Heading>

          <Paragraph>
            Within the hour, your listing will post to {networkSites}
            —we&apos;ll notify you once it&apos;s live.{' '}
            {!!daysExtended && (
              <span>It&apos;s best practice to deactivate your listing when you&apos;ve found a renter. </span>
            )}
          </Paragraph>

          {isActiveProductTypePremium && !!freemiumActiveProductDuration && (
            <Paragraph css={{ marginTop: '600' }}>
              You can still access your premium benefits for {freemiumActiveProductDuration}, regardless of listing
              status.
            </Paragraph>
          )}

          {shouldDisplayTourWizard && (
            <Paragraph
              textStyle="body-bold"
              css={{ marginBottom: '400', marginTop: '600', color: 'text.accent.blue.strong' }}
            >
              Next, set your availability so you can book tours faster and more easily.
            </Paragraph>
          )}

          {shouldDisplayTourCalendar && (
            <Paragraph
              textStyle="body-bold"
              css={{ marginBottom: '400', marginTop: '600', color: 'text.accent.blue.strong' }}
            >
              Next, make sure interested tenants can find a time you&rsquo;re free for tours by adding more availability
              to your calendar.
            </Paragraph>
          )}
        </Box>
      }
      footer={<ButtonGroup aria-label="Post activation actions">{getButtonCTA()}</ButtonGroup>}
      data-testid="PostActivationDashboardModal"
    />
  );
};

export default PostActivationDashboardModal;
